import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { combineReducers, createSlice } from '@reduxjs/toolkit';
import { xor, uniq, isArray, without } from 'lodash';
import { UiState } from 'constants/index';
import { getCatalogItemsSuccess } from 'state/items/actions';
import { addNormalizedData } from 'libs/utils/redux';
import { getNewPriceRange } from 'libs/utils/catalog';
import { stateName } from './constants';
export const initialFilterState = {
  query: null,
  catalogIds: [],
  priceFrom: null,
  priceTo: null,
  currency: null,
  colorIds: [],
  brandIds: [],
  sizeIds: [],
  materialIds: [],
  videoGameRatingIds: [],
  statusIds: [],
  sortBy: null,
  isPopularCatalog: false,
  isPersonalizationDisabled: false,
  catalogFrom: null,
  disableSearchSaving: false
};
const initialData = {
  byId: {},
  ids: []
};
export const initialDynamicFilterState = {
  dynamicFilters: initialData,
  selectedDynamicFilters: initialData,
  selectedDefaultFilters: initialData,
  temporarySelectedDynamicFilters: initialData,
  temporaryCatalogIds: [],
  temporarySizeIds: [],
  temporaryBrandIds: [],
  supportedFilters: [],
  temporaryPriceRage: {
    priceFrom: null,
    priceTo: null
  },
  filterSearchQuery: {
    text: null,
    code: null
  },
  ui: {
    uiState: UiState.Idle,
    errors: []
  }
};
export const initialDataState = {
  catalogs: initialData,
  brands: initialData,
  sizes: initialData,
  sizeGroups: initialData,
  uiState: UiState.Pending
};
export const initialSearchSessionState = {
  correlationId: '',
  sessionId: '',
  globalSearchSessionId: null,
  isStale: false
};
export const initialDtoData = {
  catalogs: [],
  brands: [],
  sizeGroups: []
};
export const initialConfigurationState = {
  config: {
    currency: '',
    locale: ''
  },
  ui: {
    uiState: UiState.Idle,
    errors: []
  }
};

const toggleSortFilterOption = (draft, action) => {
  const {
    sortBy
  } = action.payload;
  draft.sortBy = sortBy;
};

const toggleBrandFilterOption = (draft, action) => {
  const {
    id
  } = action.payload;
  draft.brandIds = xor(draft.brandIds, [id]);
};

const toggleSizeFilterOption = (draft, action) => {
  const {
    id
  } = action.payload;
  draft.sizeIds = xor(draft.sizeIds, [id]);
};

const sizeFilterSet = (draft, action) => {
  const {
    ids
  } = action.payload;
  draft.sizeIds = ids;
};

const catalogFilterSet = (draft, action) => {
  const {
    ids
  } = action.payload;
  draft.catalogIds = ids;
};

const priceRangeFilterSet = (draft, action) => {
  const {
    priceRange: {
      priceTo,
      priceFrom
    },
    currency
  } = action.payload;
  const newPriceRange = getNewPriceRange(priceFrom, priceTo);
  draft.currency = currency;
  draft.priceFrom = newPriceRange.priceFrom;
  draft.priceTo = newPriceRange.priceTo;
};

const setQuery = (draft, action) => {
  const {
    query
  } = action.payload;
  draft.query = query;
};

const applyPersonalizedSizesSuccess = (draft, action) => {
  const {
    ids
  } = action.payload;
  draft.sizeIds = uniq([...draft.sizeIds, ...ids]);
};

const changeFilters = (draft, action) => {
  const {
    filters
  } = action.payload;
  Object.assign(draft, filters);
};

const setInitialFilters = (draft, action) => {
  const {
    filters
  } = action.payload;
  Object.assign(draft, filters);
};

const resetFilters = (draft, filters) => {
  filters.forEach(filter => {
    draft[filter] = initialFilterState[filter];
  });
};

const removeFilters = (draft, action) => {
  const {
    filters
  } = action.payload;
  if (!filters.length) return initialFilterState;
  return resetFilters(draft, filters);
};

const removeFilter = (draft, action) => {
  const {
    filter: selectedFilter
  } = action.payload;
  const values = draft[selectedFilter.name];
  let newValues;

  if (isArray(values) && 'value' in selectedFilter) {
    newValues = without(values, selectedFilter.value);
  } else {
    newValues = initialFilterState[selectedFilter.name];
  } // Type assertion needed to avoid type issues, see https://github.com/microsoft/TypeScript/pull/30769


  draft[selectedFilter.name] = newValues;
};

const filtersSlice = createSlice({
  name: stateName,
  initialState: initialFilterState,
  reducers: {
    // toggle filters reducers
    toggleSortFilterOption,
    toggleBrandFilterOption,
    toggleSizeFilterOption,
    // set filters reducers
    sizeFilterSet,
    catalogFilterSet,
    priceRangeFilterSet,
    setQuery,
    submitSearchQuery: setQuery,
    applyPersonalizedSizesSuccess,
    changeFilters,
    setFilters: changeFilters,
    setInitialFilters,
    // remove filters reducers
    removeFilters,
    removeFilter
  }
});

const fetchDataRequest = draft => {
  draft.uiState = UiState.Pending;
};

const fetchDataSuccess = (draft, action) => {
  const {
    catalogs,
    brands,
    sizes,
    sizeGroups
  } = action.payload;
  draft.uiState = UiState.Success;
  addNormalizedData(draft.catalogs, catalogs);
  addNormalizedData(draft.brands, brands);
  addNormalizedData(draft.sizes, sizes);
  addNormalizedData(draft.sizeGroups, sizeGroups);
};

const fetchDataFailure = draft => {
  draft.uiState = UiState.Failure;
};

const fetchBrandsSuccess = (draft, action) => {
  const {
    brands
  } = action.payload;
  addNormalizedData(draft.brands, brands);
};

const fetchBrandSuccess = (draft, action) => {
  const {
    brand
  } = action.payload;
  const brandId = brand.id;
  draft.brands.ids = uniq([...draft.brands.ids, brandId]);
  draft.brands.byId[brandId] = brand;
};

const dataSlice = createSlice({
  name: stateName,
  initialState: initialDataState,
  reducers: {
    fetchDataRequest,
    fetchDataSuccess,
    fetchDataFailure,
    fetchBrandsSuccess,
    fetchBrandSuccess
  }
});

const setSearchSessionData = (draft, action) => {
  const {
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId
  } = action.payload;
  draft.globalSearchSessionId = globalSearchSessionId || null;
  if (!searchCorrelationId || !searchSessionId) return;
  draft.correlationId = searchCorrelationId;
  draft.sessionId = searchSessionId;
  draft.isStale = false;
};

const setIsStale = draft => {
  draft.isStale = true;
};

const searchSessionSlice = createSlice({
  name: stateName,
  initialState: initialSearchSessionState,
  reducers: {},
  extraReducers: {
    [getCatalogItemsSuccess.type]: setSearchSessionData,
    [filtersSlice.actions.changeFilters.type]: setIsStale
  }
});

const setSelectedDynamicFilter = (draft, action) => {
  const {
    selectedDynamicFilter
  } = action.payload;
  draft.selectedDynamicFilters.byId[selectedDynamicFilter.type] = selectedDynamicFilter;

  if (!draft.selectedDynamicFilters.ids.includes(selectedDynamicFilter.type)) {
    draft.selectedDynamicFilters.ids.push(selectedDynamicFilter.type);
  }
};

const setSelectedDynamicFilters = (draft, action) => {
  const {
    selectedDynamicFilters
  } = action.payload;
  draft.selectedDynamicFilters = {
    byId: {},
    ids: []
  };
  selectedDynamicFilters.forEach(selectedDynamicFilter => {
    draft.selectedDynamicFilters.byId[selectedDynamicFilter.type] = selectedDynamicFilter;
    draft.selectedDynamicFilters.ids.push(selectedDynamicFilter.type);
  });
};

const removeSelectedDynamicFilterId = (draft, action) => {
  const {
    type,
    id
  } = action.payload;
  draft.selectedDynamicFilters.byId[type].ids = draft.selectedDynamicFilters.byId[type].ids.filter(item => item !== id);
};

const removeSelectedDynamicFilterIds = (draft, action) => {
  const {
    type,
    ids
  } = action.payload;
  draft.selectedDynamicFilters.byId[type].ids = draft.selectedDynamicFilters.byId[type].ids.filter(item => !ids.includes(item));
};

const removeSelectedDynamicFilters = draft => {
  draft.selectedDynamicFilters.ids.forEach(type => {
    draft.selectedDynamicFilters.byId[type].ids = [];
  });
};

const removeSelectedDynamicFilter = (draft, action) => {
  const {
    type
  } = action.payload;
  delete draft.selectedDynamicFilters.byId[type];
  draft.selectedDynamicFilters.ids = draft.selectedDynamicFilters.ids.filter(id => id !== type);
};

const setTemporarySelectedDynamicFilter = (draft, action) => {
  const {
    temporarySelectedDynamicFilter
  } = action.payload;
  draft.temporarySelectedDynamicFilters.byId[temporarySelectedDynamicFilter.type] = temporarySelectedDynamicFilter;

  if (!draft.temporarySelectedDynamicFilters.ids.includes(temporarySelectedDynamicFilter.type)) {
    draft.temporarySelectedDynamicFilters.ids.push(temporarySelectedDynamicFilter.type);
  }
};

const setTemporarySelectedDynamicFilters = (draft, action) => {
  const {
    temporarySelectedDynamicFilters
  } = action.payload;
  draft.temporarySelectedDynamicFilters = {
    byId: {},
    ids: []
  };
  temporarySelectedDynamicFilters.forEach(temporarySelectedDynamicFilter => {
    draft.temporarySelectedDynamicFilters.byId[temporarySelectedDynamicFilter.type] = temporarySelectedDynamicFilter;
    draft.temporarySelectedDynamicFilters.ids.push(temporarySelectedDynamicFilter.type);
  });
};

const removeTemporarySelectedDynamicFilterId = (draft, action) => {
  const {
    type,
    id
  } = action.payload;
  draft.temporarySelectedDynamicFilters.byId[type].ids = draft.temporarySelectedDynamicFilters.byId[type].ids.filter(item => item !== id);
};

const removeTemporarySelectedDynamicFilterIds = (draft, action) => {
  const {
    type,
    ids
  } = action.payload;
  draft.temporarySelectedDynamicFilters.byId[type].ids = draft.temporarySelectedDynamicFilters.byId[type].ids.filter(item => !ids.includes(item));
};

const removeTemporarySelectedDynamicFilters = draft => {
  draft.temporarySelectedDynamicFilters = {
    byId: {},
    ids: []
  };
};

const removeTemporarySelectedDynamicFilter = (draft, action) => {
  const {
    type
  } = action.payload;
  delete draft.temporarySelectedDynamicFilters.byId[type];
  draft.temporarySelectedDynamicFilters.ids = draft.temporarySelectedDynamicFilters.ids.filter(id => id !== type);
};

const fetchDynamicFiltersRequest = (draft, action) => {
  const {
    supportedFilters
  } = action.payload;
  draft.ui.uiState = UiState.Pending;
  draft.supportedFilters = supportedFilters;
};

const fetchDynamicFiltersSuccess = (draft, action) => {
  const {
    dynamicFilters,
    selectedDynamicFilters
  } = action.payload;
  draft.selectedDynamicFilters = {
    byId: {},
    ids: []
  };
  draft.dynamicFilters = {
    byId: {},
    ids: []
  };
  draft.ui.errors = [];
  draft.ui.uiState = UiState.Success;
  selectedDynamicFilters === null || selectedDynamicFilters === void 0 ? void 0 : selectedDynamicFilters.forEach(selectedDynamicFilter => {
    draft.selectedDynamicFilters.byId[selectedDynamicFilter.type] = selectedDynamicFilter;
    draft.selectedDynamicFilters.ids.push(selectedDynamicFilter.type);
  });
  dynamicFilters === null || dynamicFilters === void 0 ? void 0 : dynamicFilters.forEach(dynamicFilter => {
    draft.dynamicFilters.byId[dynamicFilter.type] = dynamicFilter;
    draft.dynamicFilters.ids.push(dynamicFilter.type);
  });
};

const fetchDefaultDynamicFiltersRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const fetchDefaultDynamicFiltersSuccess = (draft, action) => {
  const {
    selectedDefaultFilters
  } = action.payload;
  draft.selectedDefaultFilters = {
    byId: {},
    ids: []
  };
  selectedDefaultFilters === null || selectedDefaultFilters === void 0 ? void 0 : selectedDefaultFilters.forEach(selectedDynamicFilter => {
    draft.selectedDefaultFilters.byId[selectedDynamicFilter.type] = selectedDynamicFilter;
    draft.selectedDefaultFilters.ids.push(selectedDynamicFilter.type);
  });
};

const fetchDefaultDynamicFiltersFailure = (draft, action) => {
  const {
    errors
  } = action.payload;
  draft.ui.errors = errors;
  draft.ui.uiState = UiState.Failure;
};

const fetchTemporaryDynamicFiltersSuccess = (draft, action) => {
  const {
    dynamicFilters,
    temporarySelectedDynamicFilters
  } = action.payload;
  draft.temporarySelectedDynamicFilters = {
    byId: {},
    ids: []
  };
  draft.dynamicFilters = {
    byId: {},
    ids: []
  };
  draft.ui.errors = [];
  draft.ui.uiState = UiState.Success;
  temporarySelectedDynamicFilters.forEach(selectedDynamicFilter => {
    draft.temporarySelectedDynamicFilters.byId[selectedDynamicFilter.type] = selectedDynamicFilter;
    draft.temporarySelectedDynamicFilters.ids.push(selectedDynamicFilter.type);
  });
  dynamicFilters.forEach(dynamicFilter => {
    draft.dynamicFilters.byId[dynamicFilter.type] = dynamicFilter;
    draft.dynamicFilters.ids.push(dynamicFilter.type);
  });
};

const fetchDynamicFiltersFailure = (draft, action) => {
  const {
    errors
  } = action.payload;
  draft.ui.errors = errors;
  draft.ui.uiState = UiState.Failure;
};

const fetchDynamicFilterSearchSuccess = (draft, action) => {
  const {
    code,
    options,
    isSelectionHighlighted
  } = action.payload;
  draft.dynamicFilters.byId[code].options = options;
  draft.dynamicFilters.byId[code].isSelectionHighlighted = isSelectionHighlighted;
};

const setTemporaryCatalogIds = (draft, action) => {
  const {
    temporaryCatalogIds
  } = action.payload;
  draft.temporaryCatalogIds = temporaryCatalogIds;
};

const removeTemporaryCatalogIds = draft => {
  draft.temporaryCatalogIds = [];
};

const setTemporarySizeIds = (draft, action) => {
  const {
    temporarySizeIds
  } = action.payload;
  draft.temporarySizeIds = temporarySizeIds;
};

const removeTemporarySizeIds = draft => {
  draft.temporarySizeIds = [];
};

const setTemporaryBrandIds = (draft, action) => {
  const {
    temporaryBrandIds
  } = action.payload;
  draft.temporaryBrandIds = temporaryBrandIds;
};

const removeTemporaryBrandIds = draft => {
  draft.temporaryBrandIds = [];
};

const setTemporaryPriceRange = (draft, action) => {
  const {
    priceRange: {
      priceTo,
      priceFrom
    }
  } = action.payload;
  const newPriceRange = getNewPriceRange(priceFrom, priceTo);
  draft.temporaryPriceRage.priceFrom = newPriceRange.priceFrom;
  draft.temporaryPriceRage.priceTo = newPriceRange.priceTo;
};

const removeTemporaryPriceRange = draft => {
  draft.temporaryPriceRage = {
    priceFrom: null,
    priceTo: null
  };
};

const setFilterSearchQuery = (draft, action) => {
  const {
    text,
    code
  } = action.payload;
  draft.filterSearchQuery.text = text;
  draft.filterSearchQuery.code = code;
};

const dynamicFilterSlice = createSlice({
  name: stateName,
  initialState: initialDynamicFilterState,
  reducers: {
    fetchDynamicFiltersRequest,
    fetchDynamicFiltersSuccess,
    fetchDynamicFiltersFailure,
    fetchTemporaryDynamicFiltersRequest: fetchDynamicFiltersRequest,
    fetchTemporaryDynamicFiltersSuccess,
    fetchTemporaryDynamicFiltersFailure: fetchDynamicFiltersFailure,
    fetchDefaultDynamicFiltersRequest,
    fetchDefaultDynamicFiltersSuccess,
    fetchDefaultDynamicFiltersFailure,
    fetchDynamicFilterSearchSuccess,
    setTemporaryDynamicFiltersState: fetchTemporaryDynamicFiltersSuccess,
    setSelectedDynamicFilter,
    setSelectedDynamicFilters,
    removeSelectedDynamicFilterId,
    removeSelectedDynamicFilterIds,
    removeSelectedDynamicFilters,
    removeSelectedDynamicFilter,
    setTemporarySelectedDynamicFilter,
    setTemporarySelectedDynamicFilters,
    removeTemporarySelectedDynamicFilterId,
    removeTemporarySelectedDynamicFilterIds,
    removeTemporarySelectedDynamicFilters,
    removeTemporarySelectedDynamicFilter,
    setTemporaryCatalogIds,
    updateTemporaryCatalogIds: setTemporaryCatalogIds,
    removeTemporaryCatalogIds,
    setTemporarySizeIds,
    removeTemporarySizeIds,
    setTemporaryBrandIds,
    removeTemporaryBrandIds,
    setTemporaryPriceRange,
    removeTemporaryPriceRange,
    setFilterSearchQuery
  },
  extraReducers: {
    [dataSlice.actions.fetchDataSuccess.type]: fetchDynamicFiltersSuccess
  }
});

const changeDtos = (draft, action) => {
  const {
    dtos
  } = action.payload;
  Object.assign(draft, dtos);
};

const dtoSlice = createSlice({
  name: stateName,
  initialState: initialDtoData,
  reducers: {
    changeDtos
  }
});

const changeIsInCatalog = (_, action) => {
  return action.payload;
};

const isInCatalogSlice = createSlice({
  name: stateName,
  initialState: false,
  reducers: {
    changeIsInCatalog
  }
});

const fetchConfigDataRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const fetchConfigDataSuccess = (draft, action) => {
  const {
    currency,
    locale
  } = action.payload;
  draft.ui.uiState = UiState.Success;
  draft.config = {
    locale,
    currency
  };
};

const fetchConfigDataFailure = draft => {
  draft.ui.uiState = UiState.Failure;
};

const configurationSlice = createSlice({
  name: stateName,
  initialState: initialConfigurationState,
  reducers: {
    fetchConfigDataRequest,
    fetchConfigDataSuccess,
    fetchConfigDataFailure
  }
});

const setCatalogTitle = (_, action) => {
  const {
    catalogTitle
  } = action.payload;
  return catalogTitle;
};

const catalogTitleSlice = createSlice({
  name: stateName,
  initialState: null,
  reducers: {},
  extraReducers: {
    [getCatalogItemsSuccess.type]: setCatalogTitle
  }
});
export const dtosReducer = dtoSlice.reducer;
export const dataReducer = dataSlice.reducer;
export const filtersReducer = filtersSlice.reducer;
export const searchSessionReducer = searchSessionSlice.reducer;
export const isInCatalogReducer = isInCatalogSlice.reducer;
export const dynamicFiltersReducer = dynamicFilterSlice.reducer;
export const catalogTitleReducer = catalogTitleSlice.reducer;
export const configurationReducer = configurationSlice.reducer;
export const actions = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, filtersSlice.actions), dataSlice.actions), dtoSlice.actions), searchSessionSlice.actions), dynamicFilterSlice.actions), isInCatalogSlice.actions), catalogTitleSlice.actions), configurationSlice.actions);
const catalogFiltersReducer = combineReducers({
  dtos: dtosReducer,
  data: dataReducer,
  filters: filtersReducer,
  dynamicFilters: dynamicFiltersReducer,
  searchSession: searchSessionReducer,
  isInCatalog: isInCatalogReducer,
  catalogTitle: catalogTitleReducer,
  configuration: configurationReducer
});
export const plug = {
  [stateName]: catalogFiltersReducer
};
export default catalogFiltersReducer;